import type { InjectionKey } from 'vue'
import type { SetOptionOpts } from 'echarts/core'
import { applyStpBasePolicy } from './presets/base'
import { applyStpLinePolicy } from './presets/line'
import { type StpChartTokens, stpChartTokens } from './tokens'
import type { StpChartOption, StpChartPreset, StpChartValueSemantics } from './types'

export const stpChartUpdateOptions: SetOptionOpts = {
  lazyUpdate: true,
  replaceMerge: ['series', 'xAxis', 'yAxis', 'dataset'],
}

export interface StpChartExtensionContext {
  tokens: StpChartTokens
  valueSemantic: StpChartValueSemantics
  reducedMotion: boolean
}

export interface StpChartExtension {
  id: string
  phase: 'before-policy' | 'after-policy'
  transform: (option: Readonly<StpChartOption>, context: Readonly<StpChartExtensionContext>) => StpChartOption
}

export interface BuildStpChartOptionInput extends Omit<StpChartExtensionContext, 'tokens'> {
  option: StpChartOption
  preset: StpChartPreset
  extensions?: StpChartExtension[]
}

export interface StpChartRuntime {
  tokens: StpChartTokens
  buildOption: (input: BuildStpChartOptionInput) => StpChartOption
  registerExtension: (extension: StpChartExtension) => void
}

export interface CreateStpChartRuntimeOptions {
  tokens?: Partial<StpChartTokens>
  extensions?: StpChartExtension[]
}

export const STP_CHART_RUNTIME_KEY: InjectionKey<StpChartRuntime> = Symbol('stp-chart-runtime')

function assertUniqueExtensions(extensions: StpChartExtension[]) {
  const ids = new Set<string>()
  for (const extension of extensions) {
    if (ids.has(extension.id))
      throw new Error(`Duplicate STP chart extension: ${extension.id}`)

    ids.add(extension.id)
  }
}

function applyExtensions(
  option: StpChartOption,
  extensions: StpChartExtension[],
  phase: StpChartExtension['phase'],
  context: StpChartExtensionContext,
) {
  return extensions
    .filter(extension => extension.phase === phase)
    .reduce((nextOption, extension) => extension.transform(nextOption, context), option)
}

function cloneChartValue<T>(value: T, seen = new WeakMap<object, unknown>()): T {
  if (Array.isArray(value)) {
    if (seen.has(value))
      return seen.get(value) as T

    const clone: unknown[] = []
    seen.set(value, clone)
    clone.push(...value.map(item => cloneChartValue(item, seen)))
    return clone as T
  }

  if (typeof value !== 'object' || value === null)
    return value

  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null)
    return value

  if (seen.has(value))
    return seen.get(value) as T

  const clone: Record<PropertyKey, unknown> = {}
  seen.set(value, clone)
  for (const key of Reflect.ownKeys(value))
    clone[key] = cloneChartValue((value as Record<PropertyKey, unknown>)[key], seen)

  return clone as T
}

export function createStpChartRuntime(options: CreateStpChartRuntimeOptions = {}): StpChartRuntime {
  const tokens = { ...stpChartTokens, ...options.tokens }
  const extensions = [...(options.extensions ?? [])]
  assertUniqueExtensions(extensions)

  return {
    tokens,
    registerExtension(extension) {
      assertUniqueExtensions([...extensions, extension])
      extensions.push(extension)
    },
    buildOption(input) {
      const allExtensions = [...extensions, ...(input.extensions ?? [])]
      assertUniqueExtensions(allExtensions)
      const context: StpChartExtensionContext = {
        tokens,
        valueSemantic: input.valueSemantic,
        reducedMotion: input.reducedMotion,
      }
      const beforePolicy = applyExtensions(cloneChartValue(input.option), allExtensions, 'before-policy', context)
      const policyOption = input.preset === 'line'
        ? applyStpLinePolicy(beforePolicy, context)
        : applyStpBasePolicy(beforePolicy, context)

      return applyExtensions(policyOption, allExtensions, 'after-policy', context)
    },
  }
}

export const defaultStpChartRuntime = createStpChartRuntime()
